import { CertificationStatusWithText } from "../enums/certifications.enum";
import { FacilityTimezoneService } from "../services/timezone/facility-timezone.service";

export const viewOnlyMode = 'viewOnly';

export const certificationConstants = {
    certificationsTitle: "Certifications",
    certificationIdKey: "certificationId",
    sendForSignature: "Send For Signature",
    uploadDocument: "Upload/Edit Document",
    edit: "Edit",
    view: "View",
    print: "Print",
    createAttestation: "Create Attestation",
    markAsHandSigned: "Mark As Hand-Signed",
    viewReasonForDecline: "View Reason for Decline",
    attestationCreated: "Attestation created successfully",
    lockedTooltipMessage: "End the Re-opened Skilled Stay to access other skilled stays"
};

export const CertificationTypes = {
  Initial: 'Initial',
  Recertification: 'Recertification',
  Attestation: 'Attestation'
} as const;

export const certificationsLandingPageConsts = {
  skilledStay: "Skilled Stay",
  status: "Status",
  endStayInfoText: "This is the last day that payment will be received for this skilled stay",
  infoIcon: "info_outline",
  viewReasonText: "Reason(s) provided by Practitioner",
  endDate: "Skilled Stay end date",
  reopenStayTitle: "Reopen Skilled Stay?",
  selectNewEndDate: "Select New End Date"
}
// Labels for Form Controls (Initial)
export const InitialLabels = {
  ADMISSION_DATE: 'Admission Date',
  USE_ADMISSION_DATE: 'Use Admission Date as Skilled Stay Start Date',
  SKILLED_STAY_START: 'Skilled Stay Start Date',
  DAILY_NEED_LABEL: 'A. The individual needs on a daily basis:',
  DESCRIBE_SERVICES_PLACEHOLDER: 'Describe services...',
  SNF_CARE_NEEDED_LABEL: 'B. SNF care is needed for condition:',
  PRIMARY_CONDITION_LABEL: 'Primary condition requiring SNF care:',
  DESCRIBE_CONDITIONS_PLACEHOLDER: 'Describe conditions...',
};

// Labels for Form Controls (Recertification)
export const RecertLabels = {
  DAILY_NEED_LABEL: 'A. The individual needs on a daily basis:',
  DESCRIBE_SERVICES_PLACEHOLDER: 'Describe services...',
  CONTINUED_NEED_LABEL: 'B. Reasons for continued need for post-hospital SNF care:',
  PRIMARY_CONDITION_LABEL: 'Primary condition requiring SNF care:',
  DESCRIBE_CONDITIONS_PLACEHOLDER: 'Describe conditions...',
  ESTIMATED_TIME_LABEL: 'C. Estimated time individual will need to remain in SNF:',
  DAYS: 'Days',
  POST_DISCHARGE_LABEL: 'D. Plans for post-discharge follow-up care:',
  REASON_OTHER: 'Reason if other',
};

// Labels for Form Controls (Attestation)
export const AttestationLabels = {
  REASON: 'Reason',
  REASON_NOTES: 'Notes',
  REASONS_PLACEHOLDER: 'Reasons...',
};

// Long Content / Static Text for Initial & Recert
export const CertificationContent = {
  DUE_INITIAL: 'Due upon First Skilled Day or Date of Admission',
  DUE_FIRST_CERT: 'Due at least 14 days from start of skilled stay',
  DUE_SUBSEQUENT_CERT: 'Due at least 30 days after previous certification',
  HEADER_424_20: 'Per § 424.20, I certify that post-hospital SNF inpatient care is required because:',
  HELPER_INPATIENT_HOSPITAL: 'For which the individual received inpatient hospital care, OR That arose while receiving SNF care for a condition for which he/she received inpatient hospital care.',
};

// Long Content / Static Text for Attestation
export const AttestationContent = {
  SALUTATION: 'To whom it may concern,',
  INTRO: 'I am writing to provide a physician attestation statement regarding the delayed certification of the medical necessity for skilled care for the following patient:',
  PATIENT_NAME: 'Patient Name:',
  DOB: 'Date of Birth:',
  MEDICARE_NUM: 'Medicare Number:',
  ADMISSION_DATE: 'Admission Date to Skilled Nursing Facility:',
  I_ATTEST_START: 'I attest that',
  WAS_UNDER_CARE: 'was under my skilled care at',
  STARTING_ON: 'starting on',
  AND_ENDING_ON: 'and ending on',
  REQUIRED_SKILLED_CARE: 'required skilled care on a daily basis in a skilled nursing facility for conditions for which they received inpatient hospital services prior to their transfer to the SNF. I ordered all the services that were delivered to my patient, and I believe they required skilled care for their entire SNF stay.',
  WAS_NOT_COMPLETED: 'was not completed in the required time frame due to:',
  UPON_REVIEW: 'However, upon review of the patient’s medical records and condition, I hereby certify that skilled care was medically necessary throughout the patient’s stay in the skilled nursing facility.',
  ATTEST_SERVICES: 'I attest that the patient required skilled nursing and/or rehabilitative services for',
  SERVICES_ESSENTIAL: 'and that such services were essential to the patient’s recovery and well-being. This certification is being submitted to comply with Medicare requirements for coverage of skilled care provided in a skilled nursing facility.',
};

export const BulkPrintModalConstants = {
  TITLE: 'Print Certification and Recertification',
  LEFT_COLUMN_TITLE: 'Select Certifications to Print',
  RIGHT_COLUMN_TITLE: 'Add Uploaded Hand Signatures',
  SELECT_ALL: 'Select All',
  ATTESTATION_LABEL: 'Attestation added',
  NO_DOCUMENTS: 'No Documents'
};

// Helper function to get due date text based on ID
export const getDueDateText = (docTypeId: number, docTypeIds: any): string => {
  switch (docTypeId) {
    case docTypeIds.Initial:
      return CertificationContent.DUE_INITIAL;
    case docTypeIds.FirstCert:
      return CertificationContent.DUE_FIRST_CERT;
    case docTypeIds.Cert:
      return CertificationContent.DUE_SUBSEQUENT_CERT;
    default:
      return '';
  }
};

export const CertRoutePaths = {
  patientCertifications: (facilityId: string, patientId: string) => 
    `/patient/${facilityId}/profile/${patientId}/documentation/certifications`,
};

export const reopenSkilledStayModalConfig = {
    title: "Reopen Skilled Stay",
    message: "Are you sure you wish to reopen the Skilled Stay?",
    buttonText: "Reopen Stay",
    hideCancelButton: false
}

export const endSkilledStayModalConfig = {
    title: "End Skilled Stay?",
    message: "Are you sure you wish to end the Skilled Stay? No further recertifications will be required.",
    buttonText: "End Stay",
    leftAlign: true,
}

export const reopenSkilledStayWithConflictModalConfig = (subsequentStayStartDate: string, subsequentStayBeforeDate: string) => {
    return {
        title: "Reopen Skilled Stay?",
        message: `There is a subsequent skilled stay dated after the skilled stay you are attempting to reopen. To reopen this skilled stay, you must select a new end date that is no later than one day before the subsequent skilled stay (beginning ${subsequentStayStartDate}).`,
        note: `If you wish to reopen the skilled stay, please select a new end date no later than ${subsequentStayBeforeDate}.`,
        buttonText: "Agree and Reopen Stay",
        leftAlign: true,
    };
};

export const deleteSkilledStayModalConfig = {
    title: "Delete Skilled Stay ?",
    message: "Are you sure you wish to delete the Skilled Stay ? All the certifications will be lost.",
    buttonText: "Delete"
}

export const viewReasonForRejectionModalConfig = {
    title: "Practitioner has declined to sign this certification",
    message: '',
    buttonText: "View Certification",
    cancelText: "Close",
    hideCancelButton: false,
}

export const certificationPDFErrorModalConfig = {
    title: "Unable to download PDF", 
    message: "PDF Not Found or Does Not Exist", 
    buttonText: "OK",
    hideCancelButton: true,
    isError: true,
}

export const editCertificationWarningModalConfig = {
    title: "Warning",
    message: "Editing this certification will remove the current signature and a new signature will be required to complete the certification",
    buttonText: "Edit",
    hideCancelButton: false,
}

export const sendForSignatureModalConfig = {
  title: "Send for Signature",
  message:
    "Are you sure you want to send this document for signature? Any edits made will be final upon submission.",
  buttonText: "Yes",
  hideCancelButton: false,
  isError: false,
  leftAlign: true,
  cancelText: "No",
  isAddPractitioner: true,
};

export const sentForSignModalConfig = {
  title: "Sent Successfully",
  message: "Certification sent to Practitioner for Signature Successfully.",
  buttonText: "Ok",
  hideCancelButton: true,
  isError: false,
}

export const handSignSuccessfulModalConfig = {
  title: "Marked as Hand Sign Successfully",
  message: "Certification has been Marked as Hand Sign Successfully.",
  buttonText: "Ok",
  hideCancelButton: true,
  isError: false,
}

export const editHandSignSuccessfulModalConfig = {
  title: "Hand Sign Edited Successfully",
  message: "Hand Sign has been edited or documents uploaded Successfully.",
  buttonText: "Ok",
  hideCancelButton: true,
  isError: false,
}

export const savedAndSentForSignModalConfig = {
  title: "Saved & Sent Successfully",
  message: "Certification has been saved & sent to Practitioner for Signature Successfully.",
  buttonText: "Ok",
  hideCancelButton: true,
  isError: false,
}

export const initialCertificationQuestionKeys = {
  admissionDate: "admissiondate",
  useAdmissionDate: "usesameadmissiondate",
  skilledStayStartDate: "skilledstaystartdate",
  dailyNeedOptionId: "dailyneedoptionids",
  dailyNeedOtherReason: "dailyneedotherreason",
  snfCondition: "snfcondition"
};

export const recertificationQuestionKeys = {
  dailyNeedOptionId: "dailyneedoptionids",
  dailyNeedOtherReason: "dailyneedotherreason",
  continuedSnfCareReason: "continuedsnfcarereason",
  estimatedDurationDays: "estimateddurationdays",
  postSnfCareOptionId: "postsnfcareoptionids",
  postSnfCareOtherReason: "postsnfcareotherreason"
};

export const attestationQuestionKeys = {
  lateReasonId: "latereasonid",
  lateReasonOther: "latereasonother",
  skilledServicesReason: "skilledservicesreason"
};

export const certificationErrors = {
  fetchSkilledStays: 'Failed to load skilled stays',
  fetchCertifications: 'Failed to load certifications',
  fetchRejectionReason: 'Failed to load rejection reason',
  addSignature: 'Failed to add signature',
  fetchDocumentData: 'Failed to load certification document data',
  createSkilledStay: 'Failed to create skilled stay',
  deleteSkilledStay: 'Failed to delete skilled stay',
  endSkilledStay: 'Failed to end skilled stay',
  reOpenSkilledStay: 'Failed to reopen skilled stay',
  createCertification: 'Failed to create new certification',
  saveCertification: 'Failed to save certification',
  fetchSkilledStayStatuses: 'Failed to fetch skilled stay statuses',
  createAttestation: 'Failed to create attestation',
  getFile: 'Failed to get the file',
  UnlockCertification : 'Failed to unlock Certification Document',
  invalidEndDate: 'Invalid End Date',
  uploadHandSigned: 'Failed to upload documents',
  fetchHandSignedDocs: 'Failed to fetch hand-signed documents',
  deleteHandSignedDoc: 'Failed to delte the document',
  renameHandSignedDoc: 'Failed to rename the document',
  getLastSignedDate: 'Failed to get the last uploaded date',
  getAllDocs: "Failed to get all documents",
  incompleteCertSendForSignature: `Certification document saved, but it is still incomplete.<br/> Unable to send for signature. Please complete all required fields and try again.`,
};

export const statusIconMap: Record<string, string> = {
  [CertificationStatusWithText.InitiatedDraft]: 'initiated',
  [CertificationStatusWithText.MissingSignature]: 'missing-signature',
  [CertificationStatusWithText.SentForSignature]: 'sent-for-signature',
  [CertificationStatusWithText.SignatureComplete]: 'signature-complete',
  [CertificationStatusWithText.HandSigned]: 'signature-complete',
  [CertificationStatusWithText.Rejected]: 'rejected',
};

export const CERTIFICATIONS_LIST_SESSION_KEYS = {
  PAGE_SIZE: 'certificationsListPageSize',
  PAGE_NUMBER: 'certificationsListPageNumber'
}

export const createSkilledStayModalConfig = {
  title: 'Create New Skilled Stay',
  message: 'New skilled stay periods cannot overlap with existing skilled stays. Please select dates in the available gaps.',
  buttonText: 'Confirm',
  cancelText: 'Cancel',
}

export const createSkilledStayModalConstants = {
  endDateRequiredForOverlap: "Please provide an End Date to ensure this stay does not overlap with an existing skilled stay.",
  dateOverlap: "The selected stay dates overlap with an existing skilled stay. Please adjust the Start Date or End Date and try again.",
  dateOrder: "The selected Start Date is greater than the End Date. Please adjust the Start Date or End Date and try again."
}